import { ImageName } from "@mcleod/images";
import { Button, ButtonVariant, Component, ContextMenu, CrudDecorator, DataSourceMode, DesignableObject, DesignerStyles, Event, KeyHandler, Label, Layout, ResourceFileProps, TabCloseEvent, Table, TableColumn } from "@mcleod/components";
import { Block, Keys, ScrollBehavior, StringUtil } from "@mcleod/core";
import { AbstractLayoutDesigner, DesignerProps, UIDesignerKeyHandlers } from "@mcleod/designer";
import { PanelPermsGrant } from "./PanelPermsGrant";
import { PermissionsDesignerTab } from "./PermissionsDesignerTab";
import { PermissionsSource } from "./PermissionsSource";
import { PermsContainer } from "./PermsContainer";
import { SecureItem } from "./SecureItem";



export interface PermissionsDesignerProps extends DesignerProps {
    permissionsSource?: PermissionsSource;
}

export class PermissionsDesigner extends AbstractLayoutDesigner<PermissionsDesignerTab> implements PermsContainer {
    panelGrants: PanelPermsGrant;
    crudDecorator: CrudDecorator;
    tabsetTools = [];
    permissionsSource: PermissionsSource;


    constructor(props?: Partial<PermissionsDesignerProps>) {
        super({
            ...props,
            wrap: false,
            endpointPath: "layout",
            localStorageManager: null,
            title: "Permissions Editor"
        });
    }

    override async onLoad(): Promise<void> {
        if (this.permissionsSource == null) {
            this.permissionsSource = new PermissionsSource();
            await this.permissionsSource.loadPermissionsData();
        }
        this.panelGrants = Layout.getLayout("common/permissions/PanelPermsGrant") as PanelPermsGrant;
        await this.panelGrants.ensureLoaded();
        this.panelGrants.permsContainer = this;
        await super.onLoad();
        this.tabset.style.overflow = "auto";
        this.add(this.tabset, this.panelGrants);

        this.tabset.addBeforeTabCloseListener((event: TabCloseEvent) => {
            if (this.tabset.getComponentCount() === 1 && this.crudDecorator == null)
                event.preventDefault();
        })
    }

    tabChanged(tab: PermissionsDesignerTab) {
        this.firstSelected?.scrollIntoView({ behavior: ScrollBehavior.INSTANT, block: Block.CENTER });
        super.tabChanged(tab);
        this.displayPermissions();
    }

    override afterTabClosed() {
        if (this.tabset.getComponentCount() === 0)
            this.crudDecorator?.slideOut();
    }

    selectComponent(object: DesignableObject): void {
        this.handleComponentSelection(object, false);
        this.displayPermissions();
    }

    private displayPermissions() {
        const comp = this.firstSelected;
        const items = SecureItem.createFromComponent(comp, this.getActiveTab());
        this.panelGrants.setItemsBeingEdited(items);

        if (this.isNestedLayoutWithGlobalPermissions(comp)) {
            this.addNestedLayoutWarningLabel((comp as Layout).layoutName);
        }
    }

    private addNestedLayoutWarningLabel(layoutName: string) {
        const layoutLabel = StringUtil.stringAfterLast(layoutName, "/");

        let text = `Permissions for the '${layoutLabel}' layout are set globally.`
        if (!this.panelGrants.selectedChildPermed) {
            text += " Toggle ON to apply permissions specifically for this page."
        }

        this.panelGrants.addWarningMessage(text);
    }

    // true when the component is a nested layout and its referenced layout has top-level permissions set
    isNestedLayoutWithGlobalPermissions(component: Component): boolean {
        return (component instanceof Layout && component.isNested === true &&
            this.permissionsSource.hasTopLevelSecureItemRows(component.layoutName));
    }

    setPropsForDeserialization(componentType: string, props: any) {
        if (componentType === "layout")
            return { ...props, applyFieldLevelPermissions: false, _designer: this };
        return props ?? {};
    }

    override createTab(props: ResourceFileProps): PermissionsDesignerTab {
        return new PermissionsDesignerTab(this, props);
    }

    override addNewTab(): PermissionsDesignerTab {
        return null;
    }

    override async openTab(props: ResourceFileProps, selectTab: boolean = true): Promise<PermissionsDesignerTab> {
        return super.openTab({ baseVersion: false, ...props }, selectTab);
    }

    override addDesignerContainerProperties(container: Component, minWidth: number, minHeight: number, width?: number): void {
        if (container?._element == null)
            return;
        container.setClassIncluded(DesignerStyles.designerContainer);
        if (container.width === undefined) {
            if (container.minWidth === undefined)
                container.element.style.minWidth = minWidth + "px";
            if (width != null)
                container.element.style.width = width + "px";
        }
        if (container.minHeight === undefined)
            container.element.style.minHeight = minHeight + "px";
    }

    override async doAfterTabDefintionLoaded(tab: PermissionsDesignerTab, def: any) {
        try {
            await this.deserializeLayout(tab);
            tab._populateComponentMap();
        } catch (error) {
            tab.addErrorLabel(error.toString());
        }
    }

    override getKeyHandlers(): KeyHandler[] {
        const result = [];
        result.push({
            key: Keys.P, modifiers: { ctrlKey: true }, listener: () => {
                if (this.selectedComponents != null && this.selectedComponents.length === 1 && !(this.firstSelected.parent instanceof PermissionsDesignerTab))
                    this.selectComponent(this.firstSelected.parent);
            }
        });

        result.push(...UIDesignerKeyHandlers.getDesignerSelectSiblingKeyListeners(this));

        return result
    }

    doAfterTabAdded(tab: PermissionsDesignerTab): void {
        if (this.crudDecorator == null && this.tabset.getComponentCount() === 1) {
            tab.closeable = false;
        }

        if (this.getActiveTab() == tab) {
            this.displayPermissions();
        }
    }

    get allowsDrop(): boolean { return false; }

    redisplayProp(propName: string, propValue: any): void { }

    override refreshPropertiesTable(): void {
    }

    addDragAndDropListeners(component: Component): void { }

    filterProps(props: any, selectedComponent: Component): void { }

    disablePropertyEditors(prop: any, editorComponents: Component[], selectedComponent: Component): void { }

    addTableColumn(table: Table): TableColumn { return null; }

    canModifyProp(propName: string, component: Component): boolean { return false; }

    componentDropped(comp: Component) { }

    applyChangeToSelectedComponents(data: any, newValue: any): void { }

    displayDataSourceTools(): void { }

    addEventHandlerFunction(component: DesignableObject, eventPropName: string): void { }

    doAfterPropChanged(component: Component, propName: string, oldValue: any, newValue: any, redisplayProp?: boolean) { }
    doBeforePropChanged(component: Component, propName: string) { }


    public static async slideUp(path: string, permissionsSource: PermissionsSource): Promise<CrudDecorator> {
        const layout = await PermissionsDesigner.createAndOpenTab(path, permissionsSource);
        if (layout == null) {
            return null;
        }
        const crudPanel = new CrudDecorator({
            layout: layout,
            mode: DataSourceMode.NONE,
            headerProps: { visible: false },
            pseudoNavigateUrl: window.location.pathname
        });
        layout.crudDecorator = crudPanel;

        layout.addLayoutLoadListener(() => {
            const closeButton = new Button({
                imageName: ImageName.close,
                variant: ButtonVariant.round,
                imageHeight: 28,
                imageWidth: 28,
                cancel: true,
                padding: 0,
                color: "primary.reverse",
                onClick: () => crudPanel.slideOut(),
                style: { position: "absolute", top: "0px", right: "0px" }
            });

            layout.panelGrants.panelHeading._element.appendChild(closeButton._element);
        })

        await crudPanel.slideIn(
            { speed: 200 }, true,
            { closeOnClickOff: false, greyedBackground: true },
            { paddingLeft: 24, paddingRight: 24, paddingTop: 0, paddingBottom: 0 }
        );
        return crudPanel;
    }

    public static async createAndOpenTab(path: string, permissionsSource: PermissionsSource): Promise<PermissionsDesigner | null> {
        return new Promise((resolve) => {
            const layout = new PermissionsDesigner({ permissionsSource });

            layout.addLayoutLoadListener(async () => {
                try {
                    const tab = await layout.openTab({ path: path }, true);
                    // return null if we could not find a layout definition for the given path
                    const result = tab.definition != null ? layout : null;
                    resolve(result);
                } catch (error) {
                    resolve(null);
                }
            });

            layout.addLoadErrorListener(() => resolve(null));
        })
    }


    showComponentContextMenu(component: Component, event: Event): void {
        // The resize listener prevents the Overlay from covering the panelGrants.
        // this allows users to still interact with panelGrants while the Overlay is open.

        const updateOverlayWidth = () => {
            const newWidth = this.getActiveTab()?.bounds?.right;
            if (overlay?.overlayDiv && newWidth)
                overlay.overlayDiv.style.width = newWidth + "px";
        };

        const resizeListener = () => updateOverlayWidth();
        this.panelGrants.addResizeListener(resizeListener);

        const menu = new ContextMenu({ sourceComponent: component, designer: this });

        menu.menuLabels?.forEach(label => {

            const isPermed = this.permissionsSource.getFieldLevelSecureItemRows(this.getActiveTab().path).some(row => {
                return row.get("item_name") === label.id;
            });

            this.getActiveTab().syncPermedStyles(label, isPermed)
        });

        menu.addUnmountListener(() => {
            this.panelGrants.removeResizeListener(resizeListener);
            this.selectComponent(component);
        });

        const overlay = menu.showInOverlay(event);
        if (overlay != null) {
            updateOverlayWidth();
            this.selectComponent(menu.menuLabels?.[0]);
        }
    }
}
